import { ReactElement, useState } from 'react';

import { useFormContext, useFormField } from '@components/form';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { MaxLengthCounter } from '@components/max-length-counter';
import { Stack } from '@components/stack';
import { TextEditor } from '@components/text-editor';
import { useScopedTranslate } from '@locale/translate';

import { getHtmlContentLength, shortDescriptionMaxLength } from 'models/jobs';

type ShortDescriptionProps = {
  description?: string;
  id: string;
  label: string;
  labelIcon?: ReactElement<IconProps>;
  placeholder?: string;
};

export const ShortDescriptionHtml = ({
  description = '',
  id,
  label,
  labelIcon,
  placeholder,
}: ShortDescriptionProps) => {
  const { t } = useScopedTranslate(ShortDescriptionLocale);
  const [contentLength, setContentLength] = useState(0);
  const shortDescriptionProps = useFormField({
    id: id,
    label: label,
    defaultValue: description,
    validators: ['required'],
    valueType: 'text-editor',
  });
  const context = useFormContext();

  return (
    <Stack space={1}>
      <Stack space={1} orientation="horizontal" align="center">
        <FormLabel
          text={shortDescriptionProps.label}
          info={t('tooltip', { length: shortDescriptionMaxLength.toString() })}
          icon={labelIcon}
        />
      </Stack>
      <TextEditor
        error={Boolean(shortDescriptionProps.error)}
        initialEditorState={description}
        onChangeHtml={(html) => {
          shortDescriptionProps.onChange(html);
          const htmlContentLength = getHtmlContentLength(html);
          setContentLength(htmlContentLength);
          if (htmlContentLength > shortDescriptionMaxLength) {
            context.store.actions.updateErrors({ shortDescription: 'error' });
          }
        }}
        variant="gray"
      >
        <TextEditor.Input
          collapseMargin
          stripPastedStyles
          disableShortcuts
          dataCy="text-editor-short-description"
          placeholder={placeholder}
        />
      </TextEditor>
      <MaxLengthCounter currentLength={contentLength} maxLength={shortDescriptionMaxLength} />
      {shortDescriptionProps.error !== 'error' && <FormError text={shortDescriptionProps.error} />}
    </Stack>
  );
};

const ShortDescriptionLocale = {
  en: {
    tooltip:
      'Short description for this job posting. Appears on career overview page, {length} characters max',
  },
  is: {
    tooltip:
      'Stutt lýsing á starfi sem birtist á yfirlitssíðu starfa hjá þínu fyrirtæki. Hámark lengd texta er {length} stafir.',
  },
};
